import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Label,
  Select,
  TextInput,
} from "flowbite-react";
import type { FC } from "react";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { HiHome } from "react-icons/hi";
import { toast, ToastContainer } from "react-toastify";
import Creatable from "react-select/creatable";
import { MultiValue } from "react-select";
import { useNavigate, useParams } from "react-router-dom";
import {
  Difficulty,
  type WorkoutDetailResponse,
  type TagResponse,
  type UpdateWorkoutRequest,
  type CreateWorkoutRequest,
} from "types/traditionalWorkouts";
import {
  createTraditionalWorkout,
  updateTraditionalWorkout,
  createWorkoutTag,
  getTraditionalWorkout,
  listWorkoutTags,
} from "../../services/traditionalWorkouts";
import { getAllTrainers } from "../../services/trainers";
import TraditionalWorkoutFileUpload from "./TraditionalWorkoutFileUpload";
import ExercisesSection, {
  type ExerciseFormItem,
  initializeExerciseKey,
} from "./ExercisesSection";
import NavbarSidebarLayout from "../../layouts/navbar-sidebar";

const toTitleCase = (str: string): string => {
  return str
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const DIFFICULTY_OPTIONS = [
  Difficulty.BEGINNER,
  Difficulty.INTERMEDIATE,
  Difficulty.ADVANCED,
];

export interface TrainerOption {
  id: number;
  title: string;
  slug: string;
}

interface TagOption {
  value: string;
  label: string;
}

interface WorkoutFormData {
  title: string;
  difficulty: Difficulty;
  duration: number;
  rounds: number;
  trainer_id: number | null;
  tag_ids: string[];
  thumbnail_image_storage_key: string | null;
  exercises: ExerciseFormItem[];
}

const getInitialFormData = (
  workout?: WorkoutDetailResponse,
): WorkoutFormData => ({
  title: workout?.title ?? "",
  difficulty: workout?.difficulty ?? Difficulty.BEGINNER,
  duration: workout?.duration ? Math.ceil(workout.duration / 60) : 30, // Convert seconds to minutes
  rounds: workout?.rounds ?? 1,
  trainer_id: workout?.trainer?.id ?? null,
  tag_ids: workout?.tags?.map((t) => t.id) ?? [],
  thumbnail_image_storage_key: workout?.thumbnail_image_storage_key ?? null,
  exercises: workout?.exercises
    ? workout.exercises
        .sort((a, b) => a.order - b.order)
        .map((item) =>
          initializeExerciseKey({
            id: item.exercise.id,
            title: item.exercise.title,
            original_video_storage_key:
              item.exercise.original_video_storage_key,
            original_video_url: item.exercise.original_video_url,
            processed_video_url: item.exercise.processed_video_url,
            video_thumbnail_url: item.exercise.video_thumbnail_url,
            video_processing_status: item.exercise.video_processing_status,
            video_duration: item.exercise.video_duration ?? null,
            exercise_duration: item.exercise.exercise_duration,
            sets: item.exercise.sets,
            reps: item.exercise.reps,
            set_duration: item.exercise.set_duration,
            rest_between_sets: item.exercise.rest_between_sets,
            notes: item.exercise.notes,
            order: item.order,
          }),
        )
    : [],
});

const TraditionalWorkoutPage: FC = function () {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isThumbnailUploading, setIsThumbnailUploading] = useState(false);
  const [workout, setWorkout] = useState<WorkoutDetailResponse | undefined>(
    undefined,
  );
  const [trainers, setTrainers] = useState<TrainerOption[]>([]);
  const [tags, setTags] = useState<TagResponse[]>([]);
  // Track if duration was manually edited by the user
  const [isManualDuration, setIsManualDuration] = useState(false);
  // Track if exercises have been modified by the user (not just loaded from API)
  const [exercisesModifiedByUser, setExercisesModifiedByUser] = useState(false);

  const [formData, setFormData] =
    useState<WorkoutFormData>(getInitialFormData());

  // Fetch trainers and tags
  const fetchTrainersAndTags = useCallback(async () => {
    try {
      const [trainersResponse, tagsResponse] = await Promise.all([
        getAllTrainers({}),
        listWorkoutTags(),
      ]);

      if (trainersResponse.data?.results?.trainers) {
        setTrainers(trainersResponse.data.results.trainers);
      }

      if (tagsResponse.results) {
        setTags(tagsResponse.results);
      }
    } catch (error) {
      console.error("Error fetching trainers and tags:", error);
    }
  }, []);

  // Fetch workout data for edit mode
  const fetchWorkout = useCallback(async () => {
    if (!id) {
      setLoading(false);
      return;
    }

    try {
      const response = await getTraditionalWorkout(id);
      if (response.results) {
        setWorkout(response.results);
        setFormData(getInitialFormData(response.results));
      }
    } catch (error) {
      console.error("Error fetching workout:", error);
      toast.error("Failed to load workout");
      navigate("/traditional-workouts");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchTrainersAndTags();
    fetchWorkout();
  }, [fetchTrainersAndTags, fetchWorkout]);

  // Auto-update workout duration when exercises change (only if user modified exercises and didn't manually set duration)
  useEffect(() => {
    // Skip auto-calculation if duration was manually set by user
    if (isManualDuration) return;
    // Skip auto-calculation if exercises haven't been modified by user (e.g., initial load from API)
    if (!exercisesModifiedByUser) return;
    if (formData.exercises.length === 0) return;

    // Calculate total workout duration from exercise durations (in seconds)
    const totalSeconds = formData.exercises.reduce((sum, exercise) => {
      return sum + (exercise.exercise_duration ?? 0);
    }, 0);

    // Convert to minutes (rounded up)
    const totalMinutes = Math.ceil(totalSeconds / 60);

    // Only update if there's a valid duration and it's different from current
    if (totalMinutes > 0 && totalMinutes !== formData.duration) {
      setFormData((prev) => ({ ...prev, duration: totalMinutes }));
    }
  }, [formData.exercises, isManualDuration, exercisesModifiedByUser]);

  const tagOptions: TagOption[] = useMemo(
    () =>
      tags.map((tag) => ({
        value: tag.id,
        label: tag.name,
      })),
    [tags],
  );

  const selectedTagOptions = useMemo(() => {
    return tagOptions.filter((option) =>
      formData.tag_ids.includes(option.value),
    );
  }, [tagOptions, formData.tag_ids]);

  const handleTagChange = (selectedOptions: MultiValue<TagOption>) => {
    setFormData((prev) => ({
      ...prev,
      tag_ids: selectedOptions.map((option) => option.value),
    }));
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    // Mark duration as manually set when user edits it
    if (name === "duration") {
      setIsManualDuration(true);
    }

    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "duration" || name === "trainer_id" || name === "rounds"
          ? value
            ? Number(value)
            : null
          : value,
    }));
  };

  const handleCreateTag = async (inputValue: string) => {
    try {
      const newTag = await createWorkoutTag({ name: inputValue });
      if (newTag.results) {
        // Update tags list
        setTags((prev) => [...prev, newTag.results!]);
        // Add to selected tags
        setFormData((prev) => ({
          ...prev,
          tag_ids: [...prev.tag_ids, newTag.results!.id],
        }));
        toast.success(`Tag "${inputValue}" created successfully`);
      }
    } catch (error) {
      console.error("Error creating tag:", error);
      toast.error("Failed to create tag");
    }
  };

  // Validation for create mode
  const isFormValid = useMemo(() => {
    return (
      formData.title.trim() !== "" &&
      formData.difficulty &&
      formData.duration > 0 &&
      formData.thumbnail_image_storage_key !== null &&
      !isThumbnailUploading
    );
  }, [formData, isThumbnailUploading]);

  // Check for changes in edit mode
  const hasChanges = useMemo(() => {
    if (!workout) return false;

    // Check basic fields
    // Handle null/undefined duration by converting to 0
    const formDurationInSeconds = (formData.duration ?? 0) * 60;
    const basicFieldsChanged =
      formData.title !== workout.title ||
      formData.difficulty !== workout.difficulty ||
      formDurationInSeconds !== workout.duration || // Convert minutes to seconds for comparison
      formData.rounds !== workout.rounds ||
      formData.trainer_id !== (workout.trainer?.id ?? null) ||
      formData.thumbnail_image_storage_key !==
        workout.thumbnail_image_storage_key ||
      JSON.stringify(formData.tag_ids.sort()) !==
        JSON.stringify((workout.tags?.map((t) => t.id) ?? []).sort());

    // Also consider as changed if duration was manually edited
    if (basicFieldsChanged || isManualDuration) return true;

    // Check exercises changes
    const originalExercises = workout.exercises || [];

    // Different number of exercises
    if (formData.exercises.length !== originalExercises.length) {
      return true;
    }

    // Compare each exercise (excluding the 'key' property which is for dnd-kit only)
    for (let i = 0; i < formData.exercises.length; i++) {
      const formExercise = formData.exercises[i];
      if (!formExercise) continue;

      const originalExercise = originalExercises.find(
        (e) => e.order === formExercise.order,
      );

      if (!originalExercise) return true;

      // Check if exercise fields have changed
      if (
        formExercise.title !== originalExercise.exercise.title ||
        formExercise.original_video_storage_key !==
          originalExercise.exercise.original_video_storage_key ||
        formExercise.exercise_duration !==
          originalExercise.exercise.exercise_duration ||
        formExercise.sets !== originalExercise.exercise.sets ||
        formExercise.reps !== originalExercise.exercise.reps ||
        formExercise.rest_between_sets !==
          originalExercise.exercise.rest_between_sets ||
        formExercise.notes !== originalExercise.exercise.notes ||
        formExercise.order !== originalExercise.order
      ) {
        return true;
      }
    }

    return false;
  }, [formData, workout, isManualDuration]);

  const canSubmit =
    (isEditMode ? hasChanges : isFormValid) && !isThumbnailUploading;

  // Block navigation when there are unsaved changes
  const hasUnsavedChanges = isEditMode ? hasChanges : isFormValid;
  const [showNavigationWarning, setShowNavigationWarning] = useState(false);
  const pendingNavigation = useRef<(() => void) | null>(null);

  // Warn before closing/refreshing the browser tab
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Override the cancel button to check for unsaved changes
  const handleCancelWithWarning = () => {
    if (hasUnsavedChanges) {
      pendingNavigation.current = () => navigate("/traditional-workouts");
      setShowNavigationWarning(true);
    } else {
      navigate("/traditional-workouts");
    }
  };

  const confirmNavigation = () => {
    setShowNavigationWarning(false);
    if (pendingNavigation.current) {
      pendingNavigation.current();
      pendingNavigation.current = null;
    }
  };

  const cancelNavigation = () => {
    setShowNavigationWarning(false);
    pendingNavigation.current = null;
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setSubmitting(true);
    try {
      if (isEditMode && workout) {
        // Build update payload with only changed fields
        const payload: UpdateWorkoutRequest = {};

        if (formData.title !== workout.title) {
          payload.title = formData.title;
        }
        if (formData.difficulty !== workout.difficulty) {
          payload.difficulty = formData.difficulty;
        }
        const durationInSeconds = formData.duration * 60;
        if (durationInSeconds !== workout.duration) {
          payload.duration = durationInSeconds; // Convert minutes to seconds
        }
        if (formData.rounds !== workout.rounds) {
          payload.rounds = formData.rounds;
        }
        if (formData.trainer_id !== (workout.trainer?.id ?? null)) {
          payload.trainer_id = formData.trainer_id;
        }
        if (
          formData.thumbnail_image_storage_key !==
          workout.thumbnail_image_storage_key
        ) {
          payload.thumbnail_image_storage_key =
            formData.thumbnail_image_storage_key || undefined;
        }
        if (
          JSON.stringify(formData.tag_ids.sort()) !==
          JSON.stringify((workout.tags?.map((t) => t.id) ?? []).sort())
        ) {
          payload.tag_ids = formData.tag_ids;
        }

        // Handle exercise changes - ALWAYS send the current list of exercises if any change occurred
        // checking deep changes on exercises is not strictly needed for the payload if we just send the list,
        // but we want to avoid sending it if it's identical.
        // However, since exercises are updated in-place, the workout relation might need update if order changed or items added/removed.

        const originalExercises = workout.exercises || [];
        const exercisesChanged =
          formData.exercises.length !== originalExercises.length ||
          formData.exercises.some((formEx) => {
            const origEx = originalExercises.find(
              (e) => e.exercise.id === formEx.id,
            );
            // If new exercise (no match), or order changed
            return !origEx || formEx.order !== origEx.order;
          });

        if (exercisesChanged) {
          payload.exercises = formData.exercises.map((ex) => {
            if (!ex.id)
              throw new Error(
                `Exercise "${ex.title}" has no ID. Please save it first.`,
              );
            return {
              exercise_id: ex.id,
              order: ex.order,
            };
          });
        }

        if (Object.keys(payload).length > 0) {
          await updateTraditionalWorkout(workout.id, payload);
          toast.success(`Workout "${formData.title}" updated successfully`);
        } else {
          toast.info("No changes to save");
        }
      } else {
        // Create mode
        const exerciseIds = formData.exercises.map((ex) => {
          if (!ex.id)
            throw new Error(
              `Exercise "${ex.title}" has no ID. Please save it first.`,
            );
          return {
            exercise_id: ex.id,
            order: ex.order,
          };
        });

        // Now create the workout with exercise references
        const payload: CreateWorkoutRequest = {
          title: formData.title,
          difficulty: formData.difficulty,
          duration: formData.duration * 60, // Convert minutes to seconds
          rounds: formData.rounds,
          exercises: exerciseIds,
        };

        if (formData.trainer_id) {
          payload.trainer_id = formData.trainer_id;
        }

        if (formData.thumbnail_image_storage_key) {
          payload.thumbnail_image_storage_key =
            formData.thumbnail_image_storage_key;
        }

        if (formData.tag_ids.length > 0) {
          payload.tag_ids = formData.tag_ids;
        }

        await createTraditionalWorkout(payload);
        toast.success("Traditional workout created successfully");
      }

      navigate("/traditional-workouts");
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} workout:`,
        error,
      );
      toast.error(
        `Failed to ${isEditMode ? "update" : "create"} traditional workout`,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = handleCancelWithWarning;

  const idPrefix = isEditMode ? "edit" : "create";

  if (loading) {
    return (
      <NavbarSidebarLayout isFooter={false}>
        <div className="flex justify-center items-center h-64">
          <div role="status" className="text-center">
            <svg
              aria-hidden="true"
              className="mx-auto my-10 size-8 animate-spin fill-blue-600 text-center text-gray-200 dark:text-gray-600"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      </NavbarSidebarLayout>
    );
  }

  return (
    <NavbarSidebarLayout isFooter={false}>
      <div className="block items-center justify-between border-b border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:flex">
        <div className="mb-1 w-full">
          <div className="mb-4">
            <Breadcrumb className="mb-4">
              <BreadcrumbItem href="/">
                <div className="flex items-center gap-x-3">
                  <HiHome className="text-xl" />
                  <span className="dark:text-white">Home</span>
                </div>
              </BreadcrumbItem>
              <BreadcrumbItem href="/traditional-workouts">
                Traditional Workouts
              </BreadcrumbItem>
              <BreadcrumbItem>{isEditMode ? "Edit" : "New"}</BreadcrumbItem>
            </Breadcrumb>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
              {isEditMode
                ? "Edit Traditional Workout"
                : "Add New Traditional Workout"}
            </h1>
          </div>
        </div>
      </div>

      <div className="p-4">
        <div className="max-w-4xl">
          <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label htmlFor={`${idPrefix}-title`}>Title</Label>
                <div className="mt-1">
                  <TextInput
                    id={`${idPrefix}-title`}
                    name="title"
                    value={formData.title}
                    placeholder="Workout title"
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="sm:col-span-2">
                <Label>Thumbnail Image</Label>
                <TraditionalWorkoutFileUpload
                  fileType="image"
                  acceptedTypes={["image/jpeg", "image/png", "image/webp"]}
                  maxSize={10 * 1024 * 1024} // 10MB
                  defaultPreview={
                    isEditMode && workout?.thumbnail_image_url
                      ? {
                          url: workout.thumbnail_image_url,
                          type: "image/jpeg",
                        }
                      : undefined
                  }
                  onUploadStart={() => setIsThumbnailUploading(true)}
                  onUploadComplete={(_, storageKey) => {
                    setFormData((prev) => ({
                      ...prev,
                      thumbnail_image_storage_key: storageKey,
                    }));
                    setIsThumbnailUploading(false);
                  }}
                  onUploadError={() => setIsThumbnailUploading(false)}
                />
              </div>

              <div>
                <Label htmlFor={`${idPrefix}-difficulty`}>Difficulty</Label>
                <div className="mt-1">
                  <Select
                    id={`${idPrefix}-difficulty`}
                    name="difficulty"
                    value={formData.difficulty}
                    onChange={handleChange}
                  >
                    {DIFFICULTY_OPTIONS.map((diff) => (
                      <option key={diff} value={diff}>
                        {toTitleCase(diff)}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor={`${idPrefix}-duration`}>
                  Duration (minutes)
                </Label>
                <div className="mt-1">
                  <TextInput
                    id={`${idPrefix}-duration`}
                    name="duration"
                    type="number"
                    min={1}
                    value={formData.duration}
                    placeholder="30"
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor={`${idPrefix}-rounds`}>Rounds</Label>
                <div className="mt-1">
                  <TextInput
                    id={`${idPrefix}-rounds`}
                    name="rounds"
                    type="number"
                    min={1}
                    value={formData.rounds}
                    placeholder="1"
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor={`${idPrefix}-trainer_id`}>Athlete</Label>
                <div className="mt-1">
                  <Select
                    id={`${idPrefix}-trainer_id`}
                    name="trainer_id"
                    value={formData.trainer_id ?? ""}
                    onChange={handleChange}
                  >
                    <option value="">No athlete</option>
                    {trainers.map((trainer) => (
                      <option key={trainer.id} value={trainer.id}>
                        {trainer.title}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>

              <div>
                <Label>Tags</Label>
                <div className="mt-1">
                  <Creatable
                    isMulti
                    options={tagOptions}
                    value={selectedTagOptions}
                    onChange={handleTagChange}
                    onCreateOption={handleCreateTag}
                    placeholder="Select or create tags..."
                    className="react-select-container"
                    classNamePrefix="react-select"
                    noOptionsMessage={() => "No tags found"}
                    formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base: any) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                    }}
                  />
                </div>
              </div>

              {/* Exercises Section */}
              <div className="sm:col-span-2 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <ExercisesSection
                  exercises={formData.exercises}
                  onChange={(exercises) => {
                    setExercisesModifiedByUser(true);
                    setFormData((prev) => ({ ...prev, exercises }));
                  }}
                />
              </div>
            </div>

            <div className="mt-6 flex gap-3 border-t border-gray-200 pt-6 dark:border-gray-700">
              <Button
                color="primary"
                disabled={submitting || !canSubmit || isThumbnailUploading}
                onClick={handleSubmit}
              >
                {isThumbnailUploading
                  ? "Uploading thumbnail..."
                  : submitting
                    ? isEditMode
                      ? "Updating..."
                      : "Creating..."
                    : isEditMode
                      ? "Update Workout"
                      : "Create Workout"}
              </Button>
              <Button color="light" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer
        position="bottom-center"
        autoClose={5000}
        hideProgressBar
        newestOnTop={false}
        rtl={false}
        pauseOnFocusLoss
        pauseOnHover
        theme="dark"
      />

      {/* Navigation warning modal */}
      {showNavigationWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              Unsaved Changes
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              You have unsaved changes. Are you sure you want to leave this
              page?
            </p>
            <div className="flex gap-3 justify-end">
              <Button color="light" onClick={cancelNavigation}>
                Stay on Page
              </Button>
              <Button color="failure" onClick={confirmNavigation}>
                Leave Page
              </Button>
            </div>
          </div>
        </div>
      )}
    </NavbarSidebarLayout>
  );
};

export default TraditionalWorkoutPage;
